import { throttle } from 'es-toolkit';
import type { ActivateFunction } from './types';

type ScrollTriggerConfig = {
  triggerName: string;
  threshold: number;
  shouldTrigger: (ratio: number, threshold: number) => boolean;
  valueToToggle: boolean;
};

const RATIO_PRECISION = 100;

export const createScrollTrigger = ({
  triggerName,
  threshold,
  shouldTrigger,
  valueToToggle
}: ScrollTriggerConfig): ActivateFunction => {
  const { documentElement } = document;

  return (toggle, triggerValue) => {
    const listener = throttle(() => {
      const scrollTop = window.pageYOffset || documentElement.scrollTop;
      const scrollableDistance =
        Math.max(documentElement.scrollHeight, documentElement.offsetHeight) -
        window.innerHeight;
      const ratio = scrollTop / scrollableDistance;
      const roundedRatio =
        Math.round(ratio * RATIO_PRECISION) / RATIO_PRECISION;

      if (!scrollableDistance || shouldTrigger(roundedRatio, threshold)) {
        toggle(triggerName, valueToToggle);
        triggerValue?.(valueToToggle);
      }
    }, 1000);

    window.addEventListener('scroll', listener, { passive: true });
    listener();

    return () => {
      listener.cancel();
      window.removeEventListener('scroll', listener);
    };
  };
};
